import type {
  ApiError,
  ContentStore,
  PageableInfo,
  PageableInfoNew,
  PageInfoCollection,
} from '@setplex/tria-api'
import type { Effect, Store, UnitTargetable } from 'effector'

import { createEvent, sample } from 'effector'
import { and, not } from 'patronum'
import { exists } from '~/shared/helpers'

type TEventForSkinParams = { page?: number; count?: number } | null

export const genPagesLogic = (
  store: Store<PageableInfo | null>,
  effectFromApi: Effect<any, any, ApiError>, // needed to check if prev request is done
  target?: UnitTargetable<any> // Unit - Event or Effect that triggers api call
) => {
  const eventForSkin = createEvent<{ page?: number; count?: number } | null>()
  const $isLast = store.map((_) => _?.last ?? false)

  sample({
    clock: eventForSkin,
    source: store,
    filter: and(not(effectFromApi.pending), not($isLast)),
    fn: (store, clockData) => {
      const {
        page: _unused1,
        count: _unused2,
        ...requestRestParams
      } = store?.params || {}
      const contentPage = exists(store?.number)
        ? Number(store?.number) + 1
        : undefined

      return {
        ...requestRestParams,
        ...clockData,
        page: clockData?.page || contentPage || 0,
        count: clockData?.count || store?.size,
      }
    },
    target: target || effectFromApi,
  })

  return eventForSkin
}

export const genPagesLogicNew = (
  store: Store<PageableInfoNew | null>,
  effectFromApi: Effect<any, any, ApiError>, // needed to check if prev request is done
  target?: UnitTargetable<any> // Unit - Event or Effect that triggers api call
) => {
  const eventForSkin = createEvent<TEventForSkinParams>()
  const $isLast = store.map((_) => _?.last ?? false)

  sample({
    clock: eventForSkin,
    source: store,
    filter: and(not(effectFromApi.pending), not($isLast)),
    fn: (pageInfo, clockData) => {
      const {
        page: _unused1,
        count: _unused2,
        ...requestRestParams
      } = pageInfo?.params || {}
      const contentPage = exists(pageInfo?.number)
        ? Number(pageInfo?.number) + 1
        : undefined

      return {
        ...requestRestParams,
        ...clockData,
        page: clockData?.page || contentPage || 0,
        count: clockData?.count || pageInfo?.size,
      }
    },
    target: target || effectFromApi,
  })

  return eventForSkin
}

export const genNestedPagesLogic = ({
  store,
  effectFromApi,
  target,
  keyName = 'categoryId',
}: {
  store: ContentStore<PageInfoCollection | null>
  effectFromApi: Effect<any, any> // needed to check if prev request is done
  target?: UnitTargetable<any> // Unit - Event or Effect that triggers api call
  keyName?: string // property name of an id that is used as key in store(PageInfoCollection)
}) => {
  const eventForSkin = createEvent<{
    page?: number
    count?: number
    [key: string]: number | undefined
  }>()

  sample({
    clock: eventForSkin,
    source: { store, pending: effectFromApi.pending },
    filter: ({ store, pending }, clockData) => {
      const categoryId = Number(clockData[keyName])
      const exactPageableInfo = (store.content || {})[categoryId]
      if (!exactPageableInfo) return false
      return !pending && exactPageableInfo.last === false
    },
    fn: ({ store }, clockData) => {
      const categoryId = Number(clockData[keyName])
      const exactPageableInfo = (store.content || {})[categoryId]
      const exactParams = exactPageableInfo.params || {}
      const {
        page: _unused1,
        count: _unused2,
        ...requestRestParams
      } = exactParams
      const contentPage = exists(exactPageableInfo.number)
        ? Number(exactPageableInfo.number) + 1
        : undefined

      return {
        ...requestRestParams,
        ...clockData,
        page: clockData.page || contentPage || 0,
        count: clockData.count || exactPageableInfo?.size,
      }
    },
    target: target || effectFromApi,
  })

  return eventForSkin
}
